import { useState } from "react";
import { createGear, listGear, retireGear, updateGear } from "../api/endpoints";
import type { GearItem } from "../api/types";
import { Button, Card, EmptyState, ErrorMessage, Input, PageHeading, Select, Spinner, Textarea } from "../components/ui";
import { useAsync } from "../hooks/useAsync";

const EMPTY_FORM = { gearType: "shoes", name: "", brand: "", model: "", purchaseDate: "", notes: "" };

export default function GearPage() {
  const [includeRetired, setIncludeRetired] = useState(false);
  const [type, setType] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingForm, setEditingForm] = useState<Partial<GearItem>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const { data, error, loading, reload } = useAsync(() => listGear({ includeRetired, type: type || undefined }), [includeRetired, type]);

  async function mutate(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setActionError(null);
    try {
      await fn();
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <Spinner />;
  if (error || !data) return <ErrorMessage error={error ?? new Error("Failed to load gear")} onRetry={reload} />;

  return (
    <div className="space-y-6" data-testid="gear-page">
      <PageHeading title="Gear" subtitle="Track shoes and equipment linked to your training." />
      {actionError && <ErrorMessage error={new Error(actionError)} />}

      <Card className="p-6">
        <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Add gear</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Type</span>
            <Select value={form.gearType} onChange={(event) => setForm({ ...form, gearType: event.target.value })} data-testid="gear-type-input">
              <option value="shoes">Shoes</option>
              <option value="pack">Pack</option>
              <option value="watch">Watch</option>
              <option value="poles">Poles</option>
            </Select>
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Name</span>
            <Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} data-testid="gear-name-input" />
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Brand</span>
            <Input value={form.brand} onChange={(event) => setForm({ ...form, brand: event.target.value })} data-testid="gear-brand-input" />
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Model</span>
            <Input value={form.model} onChange={(event) => setForm({ ...form, model: event.target.value })} data-testid="gear-model-input" />
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Purchase date</span>
            <Input type="date" value={form.purchaseDate} onChange={(event) => setForm({ ...form, purchaseDate: event.target.value })} data-testid="gear-purchase-date-input" />
          </label>
        </div>
        <label className="mt-4 block space-y-1 text-sm text-gray-600 dark:text-gray-300">
          <span>Notes</span>
          <Textarea rows={3} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} data-testid="gear-notes-input" />
        </label>
        <div className="mt-4 flex justify-end">
          <Button onClick={() => void mutate("create", async () => { await createGear({ gearType: form.gearType, name: form.name, brand: form.brand || null, model: form.model || null, purchaseDate: form.purchaseDate || null, notes: form.notes || null }); setForm(EMPTY_FORM); })} disabled={!form.name || busy !== null} data-testid="gear-create-button">Add gear</Button>
        </div>
      </Card>

      <Card className="p-4" data-testid="gear-filter-panel">
        <div className="grid gap-4 md:grid-cols-3">
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Filter type</span>
            <Input value={type} onChange={(event) => setType(event.target.value)} placeholder="shoes" data-testid="gear-filter-type-input" />
          </label>
          <label className="flex items-end gap-3 text-sm text-gray-600 dark:text-gray-300">
            <input type="checkbox" checked={includeRetired} onChange={(event) => setIncludeRetired(event.target.checked)} data-testid="gear-include-retired-input" />
            <span>Include retired</span>
          </label>
          <div className="flex items-end">
            <Button variant="secondary" onClick={reload} data-testid="gear-refresh-button">Refresh</Button>
          </div>
        </div>
      </Card>

      {data.gear.length === 0 ? (
        <EmptyState title="No gear found" description="Add your first pair of shoes or widen the filters." testId="gear-empty-state" />
      ) : (
        <div className="space-y-3" data-testid="gear-list-panel">
          {data.gear.map((item) => {
            const editing = editingId === item.id;
            return (
              <Card key={item.id} className="p-4">
                {editing ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    <Input value={String(editingForm.name ?? item.name)} onChange={(event) => setEditingForm({ ...editingForm, name: event.target.value })} data-testid="gear-edit-name-input" />
                    <Input value={String(editingForm.brand ?? item.brand ?? "")} onChange={(event) => setEditingForm({ ...editingForm, brand: event.target.value })} data-testid="gear-edit-brand-input" />
                    <Input value={String(editingForm.model ?? item.model ?? "")} onChange={(event) => setEditingForm({ ...editingForm, model: event.target.value })} data-testid="gear-edit-model-input" />
                    <Input type="date" value={String(editingForm.purchaseDate ?? item.purchaseDate ?? "")} onChange={(event) => setEditingForm({ ...editingForm, purchaseDate: event.target.value })} data-testid="gear-edit-purchase-date-input" />
                    <div className="md:col-span-2 flex gap-2">
                      <Button onClick={() => void mutate(`update-${item.id}`, async () => { await updateGear(item.id, { name: String(editingForm.name ?? item.name), brand: String(editingForm.brand ?? item.brand ?? "") || null, model: String(editingForm.model ?? item.model ?? "") || null, purchaseDate: String(editingForm.purchaseDate ?? item.purchaseDate ?? "") || null }); setEditingId(null); setEditingForm({}); })} data-testid="gear-save-button">Save</Button>
                      <Button variant="secondary" onClick={() => { setEditingId(null); setEditingForm({}); }} data-testid="gear-cancel-button">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{item.name}</h3>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-[#252525] dark:text-gray-200">{item.gearType}</span>
                        {item.retired && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">Retired</span>}
                      </div>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{[item.brand, item.model].filter(Boolean).join(" · ") || "No brand/model recorded"}</p>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{item.totalActivities} activities · {(item.totalDistanceM / 1000).toFixed(1)} km</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="secondary" onClick={() => { setEditingId(item.id); setEditingForm(item); }} data-testid="gear-edit-button">Edit</Button>
                      {!item.retired && <Button variant="secondary" onClick={() => void mutate(`retire-${item.id}`, () => retireGear(item.id))} data-testid="gear-retire-button">Retire</Button>}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
